import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BookOpen, List, Minimize2, Pause, Play, Square } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import type { ParkingNote } from '@/lib/focus/focusMachine';
import { formatEndTime } from '@/lib/focus/format';
import { useIdle } from './FocusModeLayer';
import { ParkingLot } from './ParkingLot';
import { SakuraScene } from './sakura/SakuraScene';
import type { StageActionHandlers } from './StageActions';
import { faceTime, phaseText, type FocusStageProps } from './FocusStage';

interface AmbientFocusProps
  extends StageActionHandlers,
    Pick<
      FocusStageProps,
      'view' | 'setup' | 'now' | 'endsAt' | 'sessionEndsAt'
    > {
  onExit: () => void;
  notes: ParkingNote[];
  onAddNote: (text: string) => void;
  onRemoveNote: (id: string) => void;
  /** The break's recap and activities, in the bottom-left corner. */
  breakPanel: ReactNode;
}

const glass =
  'border border-border/50 bg-card/70 text-foreground shadow-elevation-1 backdrop-blur-md';
const round = cn(
  'relative flex h-12 w-12 items-center justify-center rounded-full transition-colors duration-fast hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  glass
);

/** The parking lot as one round button; the list opens above it. */
function ParkCorner({
  notes,
  onAdd,
  onRemove,
}: Pick<AmbientFocusProps, 'notes'> & {
  onAdd: (t: string) => void;
  onRemove: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  return (
    <div ref={root} className='relative'>
      <button
        type='button'
        className={round}
        aria-label='Parking lot'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title='Park a stray thought'
      >
        <List className='h-5 w-5' aria-hidden='true' />
        {notes.length > 0 && (
          <b className='absolute -right-1 -top-1 min-w-[1.25rem] rounded-full bg-primary px-1 font-mono text-xs leading-5 text-primary-foreground'>
            {notes.length}
          </b>
        )}
      </button>
      {open && (
        <div
          role='dialog'
          aria-label='Parking lot'
          className={cn(
            'absolute bottom-14 right-0 w-[min(20rem,calc(100vw-2rem))] space-y-2 rounded-2xl p-3',
            glass
          )}
        >
          <p className='text-sm font-semibold'>Parking lot</p>
          <ParkingLot notes={notes} onAdd={onAdd} onRemove={onRemove} />
        </div>
      )}
    </div>
  );
}

/**
 * Focus mode with the sakura: the scene is the screen. The time sits in the
 * open sky; everything else waits in the corners - the way out and the round
 * at the top, what the session is for (or the break's recap) bottom left, and
 * pause, end and the parking lot bottom right. Still for 3 seconds during a
 * round, and only the scene and the time remain.
 */
export function AmbientFocus(p: AmbientFocusProps) {
  const { view } = p;
  const running = view.phase === 'focus' || view.phase === 'paused';
  const idle = useIdle(view.phase === 'focus');
  const chrome = cn(
    'absolute z-20 transition-opacity duration-700',
    idle && 'pointer-events-none opacity-0'
  );
  // The top-right corner: where the session stands, not a second copy of the time's label.
  const roundNote = view.round
    ? `Round ${view.round.current} of ${view.round.total}`
    : 'Flow';
  const meta = `${roundNote}${p.sessionEndsAt ? ` · session ends ${formatEndTime(p.now, p.sessionEndsAt)}` : ''}`;
  const next = view.round
    ? view.round.current + (view.phase === 'break' ? 1 : 0)
    : 0;
  return (
    <div className='absolute inset-0'>
      <SakuraScene progress={view.sakura} phase={view.phase} />
      {/* A soft shade under the top edge, so the time reads over cloud. */}
      <div className='pointer-events-none absolute inset-x-0 top-0 z-10 h-1/4 bg-gradient-to-b from-[rgb(25_45_100/0.22)] to-transparent' />
      <div className='pointer-events-none absolute left-1/2 top-[8%] z-10 flex -translate-x-1/2 flex-col items-center gap-1 text-center before:absolute before:-inset-x-20 before:-inset-y-12 before:-z-10 before:bg-[radial-gradient(closest-side,rgb(20_32_80/0.34),transparent)] lg:left-[22%] lg:top-[15%]'>
        <span className='font-mono text-6xl font-semibold tabular-nums tracking-tight text-white [text-shadow:0_1px_3px_rgb(20_32_80/0.35),0_2px_18px_rgb(20_32_80/0.4)] sm:text-7xl'>
          {faceTime(p)}
        </span>
        <span className='text-sm font-medium text-white [text-shadow:0_1px_2px_rgb(20_32_80/0.55),0_1px_8px_rgb(20_32_80/0.45)]'>
          {phaseText(p)}
        </span>
      </div>
      <div
        className={cn(
          chrome,
          'inset-x-4 top-4 flex items-center justify-between gap-3'
        )}
      >
        <Button
          variant='ghost'
          size='sm'
          onClick={p.onExit}
          className={cn('max-sm:h-11', 'rounded-full', glass)}
        >
          <Minimize2 className='mr-2 h-4 w-4' aria-hidden='true' />
          Exit
        </Button>
        <span
          className={cn(
            'hidden rounded-lg px-3 py-1.5 font-mono text-xs sm:block',
            glass
          )}
        >
          {meta}
        </span>
      </div>
      <div
        className={cn(
          chrome,
          'bottom-5 left-4 right-4 sm:right-auto sm:max-w-sm'
        )}
      >
        {view.phase === 'break' ? (
          <div
            className={cn(
              'max-h-[46vh] space-y-4 overflow-y-auto rounded-2xl p-4',
              glass
            )}
          >
            {p.breakPanel}
          </div>
        ) : (
          <p
            className={cn(
              'mb-16 inline-flex max-w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium sm:mb-0',
              glass
            )}
            title={view.intention}
          >
            {view.lessonId && (
              <BookOpen className='h-4 w-4 shrink-0' aria-hidden='true' />
            )}
            <span className='truncate'>
              {view.intention ?? 'One thing at a time.'}
            </span>
            {p.endsAt !== null && running && (
              <small className='shrink-0 font-mono text-xs text-muted-foreground'>
                ends {formatEndTime(p.now, p.endsAt)}
              </small>
            )}
          </p>
        )}
      </div>
      <div className={cn(chrome, 'bottom-5 right-4 flex items-end gap-2.5')}>
        {running ? (
          <>
            {view.phase === 'paused' ? (
              <button
                type='button'
                className={cn(
                  round,
                  'bg-primary text-primary-foreground hover:bg-primary'
                )}
                onClick={p.onResume}
                aria-label='Resume'
                title='Resume (Space)'
              >
                <Play className='h-5 w-5' aria-hidden='true' />
              </button>
            ) : (
              <button
                type='button'
                className={round}
                onClick={p.onPause}
                aria-label='Pause'
                title='Pause (Space)'
              >
                <Pause className='h-5 w-5' aria-hidden='true' />
              </button>
            )}
            <button
              type='button'
              className={round}
              onClick={p.onFinish}
              aria-label='End round'
              title='End round'
            >
              <Square className='h-5 w-5' aria-hidden='true' />
            </button>
            <ParkCorner
              notes={p.notes}
              onAdd={p.onAddNote}
              onRemove={p.onRemoveNote}
            />
          </>
        ) : (
          <>
            <Button
              variant={view.between ? 'default' : 'outline'}
              onClick={p.onNextBlock}
              className={cn('max-sm:h-11', !view.between && glass)}
            >
              <Play className='mr-2 h-4 w-4' aria-hidden='true' />
              {next
                ? `Start round ${next}${view.between ? '' : ' now'}`
                : 'Back to focus'}
            </Button>
            <Button
              variant='ghost'
              onClick={p.onEndSession}
              className={cn('max-sm:h-11', glass)}
            >
              End session
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
